/**
 * InfoTooltip - Elegant, reusable tooltip component for metric explanations
 * 
 * Features:
 * - Looks up definitions from centralized glossary
 * - Small, unobtrusive info icon
 * - Click or hover to reveal
 * - Supports both light and dark modes
 * - Accessible with keyboard support
 * 
 * Usage:
 *   <InfoTooltip term="overall_score" />
 *   <InfoTooltip term="hazard_score" position="bottom" />
 *   <InfoTooltip content="Custom tooltip text" />
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { LuInfo } from 'react-icons/lu'
import { createPortal } from 'react-dom'
import { cn } from '@lib/cn'
import { getGlossaryEntry } from '@/lib/glossary'

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'

interface InfoTooltipProps {
    /** Key from the glossary to lookup */
    term?: string
    /** Custom content (overrides glossary lookup) */
    content?: string
    /** Custom detail text for expanded view */
    detail?: string
    /** Preferred position (auto-adjusts if near viewport edge) */
    position?: TooltipPosition
    /** Icon size in pixels */
    size?: number
    /** Additional className for the icon container */
    className?: string
    /** Show on hover instead of click */
    triggerOnHover?: boolean
}

export function InfoTooltip({
    term,
    content,
    detail,
    position = 'top',
    size = 12,
    className,
    triggerOnHover = false,
}: InfoTooltipProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
    const triggerRef = useRef<HTMLButtonElement>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)

    // Get content from glossary if term is provided
    const glossaryEntry = term ? getGlossaryEntry(term) : undefined
    const displayContent = content || glossaryEntry?.description || 'No information available.'
    const displayDetail = detail || glossaryEntry?.details
    const displayLabel = glossaryEntry?.label
    const displayUnit = glossaryEntry?.unit
    const displayRange = glossaryEntry?.range

    // Calculate tooltip position
    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return

        const rect = triggerRef.current.getBoundingClientRect()
        const padding = 8
        const tooltipWidth = 280
        const tooltipHeight = 120 // Approximate

        let x = rect.left + rect.width / 2
        let y = rect.top

        // Adjust based on preferred position
        switch (position) {
            case 'bottom':
                y = rect.bottom + padding
                break
            case 'left':
                x = rect.left - padding
                y = rect.top + rect.height / 2
                break
            case 'right':
                x = rect.right + padding
                y = rect.top + rect.height / 2
                break
            case 'top':
            default:
                y = rect.top - padding
                break
        }

        // Viewport boundary checks
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight

        // Horizontal bounds
        if (x - tooltipWidth / 2 < padding) {
            x = tooltipWidth / 2 + padding
        } else if (x + tooltipWidth / 2 > viewportWidth - padding) {
            x = viewportWidth - tooltipWidth / 2 - padding
        }

        // Vertical bounds
        if (position === 'top' && y - tooltipHeight < padding) {
            y = rect.bottom + padding // Flip to bottom
        } else if (position === 'bottom' && y + tooltipHeight > viewportHeight - padding) {
            y = rect.top - padding // Flip to top
        }

        setTooltipPosition({ x, y })
    }, [position])

    // Handle open/close
    const handleToggle = useCallback(() => {
        if (!isOpen) {
            updatePosition()
        }
        setIsOpen(!isOpen)
    }, [isOpen, updatePosition])

    const handleMouseEnter = useCallback(() => {
        if (triggerOnHover) {
            updatePosition()
            setIsOpen(true)
        }
    }, [triggerOnHover, updatePosition])

    const handleMouseLeave = useCallback(() => {
        if (triggerOnHover) {
            setIsOpen(false)
        }
    }, [triggerOnHover])

    // Close on click outside
    useEffect(() => {
        if (!isOpen || triggerOnHover) return

        const handleClickOutside = (e: MouseEvent) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
                tooltipRef.current && !tooltipRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, triggerOnHover])

    // Close on Escape key
    useEffect(() => {
        if (!isOpen) return

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsOpen(false)
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [isOpen])

    // Update position on scroll/resize
    useEffect(() => {
        if (!isOpen) return

        const handleUpdate = () => updatePosition()
        window.addEventListener('scroll', handleUpdate, true)
        window.addEventListener('resize', handleUpdate)
        return () => {
            window.removeEventListener('scroll', handleUpdate, true)
            window.removeEventListener('resize', handleUpdate)
        }
    }, [isOpen, updatePosition])

    return (
        <>
            {/* Trigger Button */}
            <button
                ref={triggerRef}
                type="button"
                onClick={handleToggle}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={cn(
                    'inline-flex items-center justify-center rounded-full transition-all duration-200',
                    'text-[var(--color-text-tertiary)] hover:text-sky-500 dark:hover:text-sky-400',
                    'hover:bg-sky-500/10 focus:outline-none focus:ring-1 focus:ring-sky-500/50',
                    'p-0.5 ml-1 cursor-help',
                    className
                )}
                aria-label={`More information about ${displayLabel || term || 'this metric'}`}
                aria-expanded={isOpen}
            >
                <LuInfo style={{ width: size, height: size }} />
            </button>

            {/* Tooltip Portal */}
            {isOpen && createPortal(
                <div
                    ref={tooltipRef}
                    role="tooltip"
                    className={cn(
                        'fixed z-[9999] max-w-[280px] pointer-events-auto',
                        'animate-in fade-in zoom-in-95 duration-150'
                    )}
                    style={{
                        left: tooltipPosition.x,
                        top: position === 'bottom' ? tooltipPosition.y : 'auto',
                        bottom: position === 'top' ? `calc(100vh - ${tooltipPosition.y}px)` : 'auto',
                        transform: 'translateX(-50%)',
                    }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className={cn(
                        'rounded-lg shadow-xl border backdrop-blur-md',
                        'bg-white/95 dark:bg-gray-900/95',
                        'border-gray-200 dark:border-gray-700',
                        'p-3'
                    )}>
                        {/* Header with label */}
                        {displayLabel && (
                            <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-xs font-bold text-sky-600 dark:text-sky-400">
                                    {displayLabel}
                                </span>
                                {displayUnit && (
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                                        {displayUnit}
                                    </span>
                                )}
                                {displayRange && (
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                                        {displayRange}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Description */}
                        <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                            {displayContent}
                        </p>

                        {/* Details (expandable) */}
                        {displayDetail && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 leading-relaxed italic">
                                {displayDetail}
                            </p>
                        )}

                        {/* Arrow indicator */}
                        <div
                            className={cn(
                                'absolute w-2 h-2 rotate-45',
                                'bg-white dark:bg-gray-900',
                                'border-gray-200 dark:border-gray-700',
                                position === 'top' && 'bottom-[-5px] left-1/2 -translate-x-1/2 border-r border-b',
                                position === 'bottom' && 'top-[-5px] left-1/2 -translate-x-1/2 border-l border-t',
                            )}
                        />
                    </div>
                </div>,
                document.body
            )}
        </>
    )
}

export default InfoTooltip
